import React, { useEffect, useMemo, useState } from "react";
import "./SchoolV4.css";
import engineeringStudents from "../assets/img/indian-engineering-students.webp";
import learningExperience from "../assets/img/indian-learning-experience.webp";

const programmes = [
  {
    level: "Undergraduate",
    name: "B.Tech – Computer Science & Engineering",
    duration: "4 Years",
    eligibility: "10+2 with PCM",
  },
  {
    level: "Undergraduate",
    name: "B.Tech – Mechanical Engineering",
    duration: "4 Years",
    eligibility: "10+2 with PCM",
  },
  {
    level: "Undergraduate",
    name: "B.Tech – Civil Engineering",
    duration: "4 Years",
    eligibility: "10+2 with PCM",
  },
  {
    level: "Undergraduate",
    name: "B.Tech – Electrical & Electronics Engineering",
    duration: "4 Years",
    eligibility: "10+2 with PCM",
  },
  {
    level: "Postgraduate",
    name: "M.Tech – Computer Science & Engineering",
    duration: "2 Years",
    eligibility: "Relevant B.E./B.Tech",
  },
  {
    level: "Postgraduate",
    name: "M.Tech – Civil Engineering",
    duration: "2 Years",
    eligibility: "Relevant B.E./B.Tech",
  },
  {
    level: "Diploma",
    name: "Diploma – Computer Science Engineering",
    duration: "3 Years",
    eligibility: "As per University norms",
  },
  {
    level: "Diploma",
    name: "Diploma – Mechanical Engineering",
    duration: "3 Years",
    eligibility: "As per University norms",
  },
  {
    level: "Doctoral",
    name: "Ph.D. – Engineering & Technology",
    duration: "As per norms",
    eligibility: "Relevant postgraduate degree",
  },
];

const labs = [
  ["AI & Machine Learning Lab", "Intelligent systems, model development and data-driven experimentation."],
  ["Programming & Cloud Lab", "Software development, cloud platforms and collaborative coding environments."],
  ["Mechanical Engineering Workshop", "Fabrication, manufacturing processes, measurement and prototyping."],
  ["Civil Material Testing Lab", "Concrete, soil, structures and construction-material investigation."],
  ["Electrical Machines Lab", "Motors, generators, transformers, power systems and control experiments."],
  ["Innovation & Incubation Studio", "Student projects, startup ideas and interdisciplinary problem-solving."],
];

const academicResources = [
  ["Academic Calendar", "View semester schedules, teaching periods and key academic dates.", "/academic-calendar"],
  ["Curriculum & Syllabus", "Access programme structures, course outcomes and detailed syllabi.", "/syllabus"],
  ["Academic Regulations", "Understand assessment, progression and programme requirements.", "/academic-rules"],
  ["Student Handbook", "Find academic processes, support services and campus guidance.", "/student-handbook"],
  ["Faculty Directory", "Connect with faculty members and explore their academic expertise.", "/faculty"],
  ["Downloads", "Access brochures, forms, notices and programme information.", "/downloads"],
];

const stories = [
  {
    type: "Placement",
    title: "From coding practice to a confident technology career",
    text: "Structured technical preparation, project exposure and mentoring helped this student approach recruitment with clarity.",
  },
  {
    type: "Innovation",
    title: "A classroom idea developed into a working prototype",
    text: "An interdisciplinary student team transformed a local challenge into an applied engineering solution.",
  },
  {
    type: "Research",
    title: "Student-faculty collaboration addressing sustainable infrastructure",
    text: "Applied investigation connected engineering fundamentals with the needs of growing Indian communities.",
  },
];

const updates = [
  ["28 JUL", "Workshop", "Industry-led workshop on emerging applications of Artificial Intelligence"],
  ["18 JUL", "Achievement", "Engineering students recognised at an inter-university innovation challenge"],
  ["05 JUL", "Visit", "Industrial visit connects classroom concepts with manufacturing practice"],
];

function SectionHeader({ label, title, text, action }) {
  return (
    <header className="s4-section-header">
      <div>
        <span>{label}</span>
        <h2>{title}</h2>
        {text && <p>{text}</p>}
      </div>
      {action && <a href={action.href}>{action.label} <i>↗</i></a>}
    </header>
  );
}

export default function SchoolV4() {
  const [programmeLevel, setProgrammeLevel] = useState("All");
  const [activeStory, setActiveStory] = useState(0);
  const [activeFaq, setActiveFaq] = useState(0);

  const filteredProgrammes = useMemo(
    () =>
      programmeLevel === "All"
        ? programmes
        : programmes.filter((programme) => programme.level === programmeLevel),
    [programmeLevel]
  );

  useEffect(() => {
    const sections = document.querySelectorAll(".school-v4 [data-reveal]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("s4-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -35px" }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const faqs = [
    ["Which engineering programmes are available?", "The School offers undergraduate, postgraduate, diploma and doctoral pathways. Replace the demo programme list with current University-approved offerings."],
    ["What is the eligibility for admission?", "Eligibility varies by programme. The programme table provides a short overview; applicants should confirm the latest official requirements before applying."],
    ["Does the curriculum include practical learning?", "Yes. Laboratory sessions, workshops, industrial exposure, projects and capstone work are integrated according to programme requirements."],
    ["What career support is provided?", "Career support includes aptitude preparation, technical training, communication development, résumé guidance, mock interviews and recruitment opportunities."],
  ];

  return (
    <main className="school-v4">
      <section className="s4-banner">
        <img src={engineeringStudents} alt="Indian engineering students building a robotics project" />
        <div className="s4-banner__overlay" />
        <div className="s4-shell s4-banner__content">
          <nav><a href="/">Home</a><span>/</span><a href="/academics">Schools</a><span>/</span><b>Engineering & Technology</b></nav>
          <span className="s4-banner__label">ISBM University · Chhattisgarh</span>
          <h1>School of Engineering<br />& Technology</h1>
          <p>Engineering knowledge. Practical capability. Responsible innovation.</p>
        </div>
        <aside className="s4-banner__apply">
          <span>Admissions 2026</span>
          <b>Begin your application</b>
          <a href="/apply-online">Apply online ↗</a>
        </aside>
      </section>

      <section className="s4-command-bar">
        <div className="s4-shell">
          <a href="/apply-online"><span>01</span><b>Apply Online</b><i>↗</i></a>
          <a href="/download-brochure"><span>02</span><b>Download Brochure</b><i>↓</i></a>
          <a href="/admission-helpdesk"><span>03</span><b>Admission Helpdesk</b><i>↗</i></a>
          <a href="/campus-visit"><span>04</span><b>Book Campus Visit</b><i>↗</i></a>
        </div>
      </section>

      <nav className="s4-school-nav" aria-label="School navigation">
        <div className="s4-shell">
          {[
            ["Overview", "overview"],
            ["Programmes", "programmes"],
            ["Placements", "placements"],
            ["Learning", "learning"],
            ["Laboratories", "laboratories"],
            ["Research", "research"],
            ["Resources", "resources"],
            ["Updates", "updates"],
          ].map(([label, id]) => <a href={`#${id}`} key={id}>{label}</a>)}
        </div>
      </nav>

      <section className="s4-overview s4-section" id="overview" data-reveal>
        <div className="s4-shell s4-overview__layout">
          <aside className="s4-index">
            <span>On this page</span>
            <a href="#overview">About the School</a>
            <a href="#programmes">Programmes</a>
            <a href="#placements">Placement outcomes</a>
            <a href="#learning">Teaching & learning</a>
            <a href="#laboratories">Laboratories</a>
            <a href="#resources">Academic resources</a>
          </aside>

          <div className="s4-overview__main">
            <SectionHeader label="Overview" title="Engineering education connected to India’s future." />
            <div className="s4-overview__columns">
              <p className="s4-overview__lead">
                The School of Engineering & Technology develops technically
                capable and socially responsible professionals through
                rigorous academics, application-led learning and industry exposure.
              </p>
              <div>
                <p>
                  Students build strong disciplinary foundations while engaging
                  with emerging technology, sustainability, entrepreneurship and
                  Indian Knowledge Systems. The learning environment encourages
                  curiosity, teamwork and practical problem-solving.
                </p>
                <p>
                  Faculty mentoring, laboratories, projects and professional
                  preparation help learners connect classroom knowledge with
                  real engineering contexts.
                </p>
              </div>
            </div>
            <div className="s4-overview__facts">
              <article><strong>12+</strong><span>Programmes</span></article>
              <article><strong>30+</strong><span>Labs & workshops</span></article>
              <article><strong>250+</strong><span>Recruiting partners*</span></article>
              <article><strong>95%</strong><span>Placement assistance*</span></article>
            </div>
          </div>
        </div>
      </section>

      <section className="s4-credentials" data-reveal>
        <div className="s4-shell">
          <div className="s4-credentials__intro">
            <span>Academic strengths</span>
            <h2>Built around outcomes,<br />not only classrooms.</h2>
          </div>
          <div className="s4-credentials__list">
            {[
              ["NEP-aligned", "Flexible, multidisciplinary and outcome-focused curriculum"],
              ["Industry exposure", "Internships, visits, expert sessions and applied assignments"],
              ["Project learning", "Mini-projects and capstone work linked to real challenges"],
              ["Career preparation", "Technical, aptitude and professional communication training"],
              ["Innovation culture", "Ideation, prototypes, research and entrepreneurial support"],
              ["Student mentoring", "Academic guidance and personal development support"],
            ].map(([title, text], index) => (
              <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><div><b>{title}</b><p>{text}</p></div></article>
            ))}
          </div>
        </div>
      </section>

      <section className="s4-programmes s4-section" id="programmes" data-reveal>
        <div className="s4-shell">
          <SectionHeader
            label="Academic programmes"
            title="Find the programme that fits your next step."
            text="Compare programme level, duration and general eligibility in one clear view."
            action={{ label: "View all programmes", href: "/programmes" }}
          />

          <div className="s4-programme-filter" role="tablist">
            {["All", "Undergraduate", "Postgraduate", "Diploma", "Doctoral"].map((level) => (
              <button
                key={level}
                type="button"
                role="tab"
                aria-selected={programmeLevel === level}
                className={programmeLevel === level ? "active" : ""}
                onClick={() => setProgrammeLevel(level)}
              >
                {level}
              </button>
            ))}
          </div>

          <div className="s4-programme-table">
            <div className="s4-programme-table__head">
              <span>Programme</span><span>Level</span><span>Duration</span><span>Eligibility</span><span />
            </div>
            {filteredProgrammes.map((programme) => (
              <a href="/programme-details" className="s4-programme-row" key={programme.name}>
                <b>{programme.name}</b><span>{programme.level}</span><span>{programme.duration}</span>
                <span>{programme.eligibility}</span><i>↗</i>
              </a>
            ))}
          </div>
          <p className="s4-small-note">Eligibility shown is indicative. Confirm the latest approved criteria before publishing.</p>
        </div>
      </section>

      <section className="s4-placement" id="placements" data-reveal>
        <div className="s4-shell s4-placement__grid">
          <div className="s4-placement__proof">
            <span>Placement snapshot</span>
            <h2>Career outcomes presented with clarity.</h2>
            <p>
              The School combines academic learning with structured career
              preparation, recruiter engagement and opportunities for industry exposure.
            </p>
            <a href="/placements">View complete placement report ↗</a>
          </div>
          <div className="s4-placement__metrics">
            <article><strong>45 LPA</strong><span>Highest offer*</span></article>
            <article><strong>25 LPA</strong><span>Average offer*</span></article>
            <article><strong>250+</strong><span>Companies hired*</span></article>
            <article><strong>6500+</strong><span>Placements*</span></article>
          </div>
        </div>
        <div className="s4-recruiter-strip">
          <span>Technology</span><span>Infrastructure</span><span>Manufacturing</span><span>Consulting</span>
          <span>Energy</span><span>IT Services</span><span>Research</span><span>Startups</span>
        </div>
      </section>

      <section className="s4-learning s4-section" id="learning" data-reveal>
        <div className="s4-shell">
          <SectionHeader label="Teaching & learning" title="One learning ecosystem. Multiple ways to grow." />
          <div className="s4-learning__layout">
            <img src={learningExperience} alt="Indian students learning with a faculty mentor" loading="lazy" />
            <div className="s4-learning__methods">
              {[
                ["01", "Experiential learning", "Learn through laboratories, visits and hands-on activity."],
                ["02", "Project-based learning", "Work in teams to design solutions for authentic challenges."],
                ["03", "Interdisciplinary learning", "Connect engineering knowledge with business, society and sustainability."],
                ["04", "Evidence-based learning", "Use research, observation and data to support decisions."],
                ["05", "Participatory learning", "Contribute through discussion, presentation and peer collaboration."],
                ["06", "Self-directed learning", "Develop ownership, curiosity and continuous learning habits."],
              ].map(([number, title, text]) => (
                <article key={number}><span>{number}</span><div><b>{title}</b><p>{text}</p></div></article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="s4-labs s4-section" id="laboratories" data-reveal>
        <div className="s4-shell">
          <SectionHeader
            label="Laboratories & infrastructure"
            title="Specialist spaces for practical understanding."
            text="Laboratories are presented as an indexed academic resource instead of decorative image cards."
            action={{ label: "Explore all facilities", href: "/facilities" }}
          />
          <div className="s4-labs__directory">
            {labs.map(([title, text], index) => (
              <a href="/facilities" key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div><b>{title}</b><p>{text}</p></div><i>View lab ↗</i>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="s4-stories" id="research" data-reveal>
        <div className="s4-shell s4-stories__grid">
          <div className="s4-stories__menu">
            <span>Student outcomes</span>
            {stories.map((story, index) => (
              <button
                type="button"
                className={activeStory === index ? "active" : ""}
                onClick={() => setActiveStory(index)}
                key={story.title}
              >
                <span>0{index + 1}</span><b>{story.type}</b>
              </button>
            ))}
          </div>
          <article className="s4-stories__feature">
            <span>{stories[activeStory].type}</span>
            <h2>{stories[activeStory].title}</h2>
            <p>{stories[activeStory].text}</p>
            <a href="/student-stories">Read the story ↗</a>
          </article>
          <div className="s4-stories__image">
            <img src={engineeringStudents} alt="Indian engineering student outcome story" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="s4-resources s4-section" id="resources" data-reveal>
        <div className="s4-shell">
          <SectionHeader label="Academic resources" title="The information students need, easy to find." />
          <div className="s4-resources__grid">
            {academicResources.map(([title, text, link], index) => (
              <a href={link} key={title}><span>{String(index + 1).padStart(2, "0")}</span><b>{title}</b><p>{text}</p><i>↗</i></a>
            ))}
          </div>
        </div>
      </section>

      <section className="s4-updates s4-section" id="updates" data-reveal>
        <div className="s4-shell">
          <SectionHeader
            label="School updates"
            title="News, events and academic activity."
            action={{ label: "View all updates", href: "/news-events" }}
          />
          <div className="s4-updates__list">
            {updates.map(([date, type, title]) => (
              <a href="/news-events" key={title}><time>{date}</time><span>{type}</span><b>{title}</b><i>↗</i></a>
            ))}
          </div>
        </div>
      </section>

      <section className="s4-faq s4-section" data-reveal>
        <div className="s4-shell s4-faq__grid">
          <div><span>Frequently asked questions</span><h2>Before you apply.</h2><p>Clear answers for students and families exploring engineering education at ISBM University.</p></div>
          <div className="s4-faq__list">
            {faqs.map(([question, answer], index) => {
              const open = activeFaq === index;
              return (
                <article className={open ? "open" : ""} key={question}>
                  <button type="button" aria-expanded={open} onClick={() => setActiveFaq(open ? -1 : index)}>
                    <span>0{index + 1}</span><b>{question}</b><i>{open ? "−" : "+"}</i>
                  </button>
                  <div><p>{answer}</p></div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="s4-enquiry">
        <div className="s4-shell s4-enquiry__grid">
          <div><span>Admissions 2026</span><h2>Ready to study engineering?</h2><p>Connect with the admissions team for programme and eligibility guidance.</p></div>
          <div className="s4-enquiry__actions">
            <a href="/apply-online">Apply online <i>↗</i></a>
            <a href="tel:+919373199999">Call +91-9373199999 <i>↗</i></a>
            <a href="/campus-visit">Book a campus visit <i>↗</i></a>
          </div>
        </div>
      </section>
    </main>
  );
}
